import { notNullOrUndefined } from "@core/utils";
import {
  AmountFieldSchema,
  ArrayFieldSchema,
  CheckboxGroupFieldSchema,
  CollapseSettings,
  DateFieldSchema,
  FieldSchema,
  FileFieldSchema,
  FileManagementFieldSchema,
  FormControlDependsOnSchema,
  FormControlOnChangeSchema,
  FormControlUiSchema,
  FormFieldSchema,
  IArrayValidation,
  IValidation,
  ObjectFieldSchema,
  RadioBoxFieldSchema,
  ReadOnlyFieldSchema,
  Schema,
  SelectFieldSchema,
  TextAreaFieldSchema,
  TextFieldSchema
} from "@generation/core";
import {
  ArrayItemsRequiredValidation,
  CustomErrorMessage,
  HasFileExtensionValidation,
  HasFileMaxSizeValidation,
  HasMaxFilesUploadValidation,
  IsFileNotEmptyValidation
} from "@generation/form";
import { IPopupHint } from "@shared/common/src/Interfaces/IPopupHint";
import ReactHtmlParser from "html-react-parser";
import { AbstractFieldModel } from "../client/model/fields/abstractFieldModel";
import { ArrayFieldModel } from "../client/model/fields/arrayFieldModel";
import { CheckboxGroupFieldModel } from "../client/model/fields/checkboxGroupFieldModel";
import { DateFieldModel } from "../client/model/fields/dateFieldModel";
import { FileFieldModel, FileManagementFieldModel } from "../client/model/fields/fileFieldModel";
import { NumberFieldModel } from "../client/model/fields/numberFieldModel";
import { ObjectFieldModel } from "../client/model/fields/objectFieldModel";
import { RadioFieldModel } from "../client/model/fields/radioFieldModel";
import { ReadonlyFieldModel } from "../client/model/fields/readOnlyFieldModel";
import { SelectFieldModel } from "../client/model/fields/selectFieldModel";
import { TextAreaFieldModel } from "../client/model/fields/textAreaFieldModel";
import { TextFieldModel } from "../client/model/fields/textFieldModel";
import { OnChangeModel } from "../client/model/onChangeModel";
import { SelectFieldOrderBy } from "../client/model/options/selectFieldOrderByEnum";
import { IPopupHint as jsonPopupHint } from "../client/model/popupHint";
import { UIModel } from "../client/model/uiModel";
import { ValidateField } from "../client/model/validation/validateField";
import { AllFieldTypes } from "../client/types/fieldTypes";
import { ConfiguratieService } from "../service/configuratieService";
import { DependsOnMapper } from "./DependsOnMapper";
import { FieldConverters } from "./FieldConverters";
import { convertValidate } from "./ValidateMapper";

export class FormSchemaModelMapper {
  getValueOrEmpty = (value: string | null | undefined) => value ?? "";
  valueToUndefinedOrValue = (value: any | null | undefined) => value ?? undefined;

  constructor(private readonly apiUrl: string) {
    //
  }

  map = (formSchema: { [key: string]: AllFieldTypes }): Schema => {
    const result: Schema = {};

    Object.getOwnPropertyNames(formSchema).forEach((propertyName) => {
      const property = formSchema[propertyName];

      const schema = this.mapField(property);
      if (schema) {
        result[propertyName] = schema;
      }
    });

    return result;
  };

  mapField = (field: AllFieldTypes): FieldSchema | undefined => {
    const fieldConverterMap = new FieldConverters();

    fieldConverterMap.registerConverter("object", this.convertObjectFieldModel);
    fieldConverterMap.registerConverter("array", this.convertArrayFieldModel);
    fieldConverterMap.registerConverter("date", this.convertDateFieldModel);
    fieldConverterMap.registerConverter("checkbox-group", this.convertCheckboxFieldModel);
    fieldConverterMap.registerConverter("radio", this.convertRadioFieldModel);
    fieldConverterMap.registerConverter("select", this.convertSelectFieldModel);
    fieldConverterMap.registerConverter("number", this.convertAmountFieldModel);
    fieldConverterMap.registerConverter("textarea", this.convertTextareaFieldModel);
    fieldConverterMap.registerConverter("text", this.convertTextFieldModel);
    fieldConverterMap.registerConverter("file", this.convertFileFieldModel);
    fieldConverterMap.registerConverter("file-management", this.convertFileManagementFieldModel);
    fieldConverterMap.registerConverter("readonly", this.convertReadonlyFieldModel);
    // hidden field nooit hier tonen!

    return fieldConverterMap.mapField(field);
  };

  isField = <Type extends AbstractFieldModel>(model: AbstractFieldModel, type: string): model is Type => {
    return model.type === type;
  };

  setFormFieldBaseProperties = <TFieldSchema extends FormFieldSchema>(
    schema: TFieldSchema,
    field: AbstractFieldModel,
    validate?: IValidation[]
  ): TFieldSchema => {
    return {
      ...schema,
      ...this.setBaseProperties(schema, field),
      validate: () => [
        ...(validate || []),
        ...(field.validate || field.onChange ? convertValidate(field, schema.type) : [])
      ],
      infoText: field.infoText,
      disabled: field.disabled,
      onChange: field.onChange ? this.convertOnChange(field.onChange) : undefined,
      popupHint: this.convertPopupHint(field.popupHint),
      showIsValidCheckmark: field.showIsValidCheckmark
    };
  };

  setBaseProperties = <TFieldSchema extends FieldSchema>(
    schema: TFieldSchema,
    field: AbstractFieldModel
  ): TFieldSchema => {
    return {
      ...schema,
      dependsOn: this.convertDependsOn(field),
      label: this.getValueOrEmpty(field.label),
      className: this.getValueOrEmpty(field.ui?.className),
      ui: field.ui ? this.convertUi(field.ui) : undefined,
      beforeFieldContent: this.convertBeforeFieldContent(field.beforeFieldContent)
    };
  };

  convertArrayFieldModel = (property: ArrayFieldModel): ArrayFieldSchema => {
    const defaultObject: ObjectFieldSchema = { type: "object", properties: {} };
    const items = property.items ? this.mapField(property.items) : defaultObject;

    const schema: ArrayFieldSchema = {
      type: "array",
      addLabel: this.getValueOrEmpty(property.addLabel),
      validate: property.validate ? this.convertArrayValidation(property.validate) : undefined,
      items: items ?? defaultObject,
      maximumNumberOfRows: property.maximumNumberOfRows ?? undefined
    };

    return this.setBaseProperties(schema, property);
  };

  convertAmountFieldModel = (field: NumberFieldModel): AmountFieldSchema => {
    const schema: AmountFieldSchema = {
      type: "amount",
      decimalScale: field.decimalsEnabled ? 2 : 0,
      icon: field.icon,
      overview: {
        beforeText: field.overview?.beforeText
      }
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertObjectFieldModel = (property: ObjectFieldModel): ObjectFieldSchema => {
    const schema: ObjectFieldSchema = {
      type: "object",
      properties: this.map(
        property.properties as {
          [key: string]: AbstractFieldModel;
        }
      )
    };

    return this.setBaseProperties(schema, property);
  };

  convertTextFieldModel = (field: TextFieldModel): TextFieldSchema => {
    const schema: TextFieldSchema = {
      type: "text",
      autoComplete: field.autoComplete
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertReadonlyFieldModel = (field: ReadonlyFieldModel): ReadOnlyFieldSchema => {
    const schema: ReadOnlyFieldSchema = {
      type: "readOnly"
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertFileFieldModel = (field: FileFieldModel): FileFieldSchema => {
    const schema: FileFieldSchema = {
      type: "file",
      buttonText: field.buttonText,
      multiple: field.multiple,
      maxFiles: field.maxFiles,
      acceptFileTypes: field.extensions?.map((item) => `.${item}`).join(","),
      fileIndicatorLabel: field.fileIndicatorLabel,
      showFileIndicatorLabel: field.showFileIndicatorLabel,
      showValidationMessageBelowFile: field.showValidationMessageBelowFile
    };

    let validation: IValidation[] = this.getFileValidation(field);
    return this.setFormFieldBaseProperties(schema, field, validation);
  };

  convertFileManagementFieldModel = (field: FileManagementFieldModel): FileManagementFieldSchema => {
    const schema: FileManagementFieldSchema = {
      type: "file-management",
      buttonText: field.buttonText,
      dropFilesText: field.dropFilesText,
      acceptFileTypes: field.extensions?.map((item) => `.${item}`).join(","),
      fileIndicatorLabel: field.fileIndicatorLabel,
      showFileIndicatorLabel: field.showFileIndicatorLabel
    };

    let validation: IValidation[] = this.getFileValidation(field);
    return this.setFormFieldBaseProperties(schema, field, validation);
  };

  convertTextareaFieldModel = (field: TextAreaFieldModel): TextAreaFieldSchema => {
    const schema: TextAreaFieldSchema = {
      type: "textArea"
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertDateFieldModel = (field: DateFieldModel): DateFieldSchema => {
    const schema: DateFieldSchema = {
      type: "date"
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertRadioFieldModel = (field: RadioFieldModel): RadioBoxFieldSchema => {
    const schema: RadioBoxFieldSchema = {
      type: "radio",
      options: field.options?.map((item) => ({
        id: this.getValueOrEmpty(item.value),
        value: this.getValueOrEmpty(item.value),
        label: this.getValueOrEmpty(item.label)
      }))
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertCheckboxFieldModel = (field: CheckboxGroupFieldModel): CheckboxGroupFieldSchema => {
    const schema: CheckboxGroupFieldSchema = {
      type: "checkbox-group",
      options:
        field.options?.map((item) => ({
          id: this.getValueOrEmpty(item.value),
          value: this.getValueOrEmpty(item.value),
          label: this.getValueOrEmpty(item.label)
        })) ?? []
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  mapOrderedOptions = (
    options: {
      id: string;
      value: string;
      label: string;
    }[],
    orderOptionsBy: {
      orderBy: SelectFieldOrderBy;
      exclusions?: string[];
    }
  ): { id: string; value: string; label: string }[] => {
    let sortedOptions = options;

    if (orderOptionsBy.orderBy === SelectFieldOrderBy.Alphabetical) {
      const exclusions = orderOptionsBy.exclusions ?? [];
      const exclusionsItems = options.filter((item) => exclusions.includes(item.value));
      const selectOptionsWithoutExclusions = options.filter((item) => !exclusions.includes(item.value));
      const sortedItems = selectOptionsWithoutExclusions.sort((a, b) => a.label.localeCompare(b.label));
      sortedOptions = [...sortedItems, ...exclusionsItems];
    }

    return sortedOptions;
  };

  convertSelectFieldModel = (field: SelectFieldModel): SelectFieldSchema => {
    const mapSelectOptions = field.options?.map((item) => ({
      id: this.getValueOrEmpty(item.value),
      value: this.getValueOrEmpty(item.value),
      label: this.getValueOrEmpty(item.label)
    }));
    const orderOptionsBy = field.orderOptionsBy;

    const schema: SelectFieldSchema = {
      type: "select",
      options:
        mapSelectOptions && orderOptionsBy
          ? this.mapOrderedOptions(mapSelectOptions, orderOptionsBy)
          : mapSelectOptions,
      optionToContentAfterText: field.optionToContentAfterText,
      datasource: field.datasource
        ? {
            key: this.getValueOrEmpty(field.datasource.key),
            datasourceParams1: this.valueToUndefinedOrValue(field.datasource.datasourceParams1),
            datasourceParams2: this.valueToUndefinedOrValue(field.datasource.datasourceParams2)
          }
        : undefined,
      multiSelect: this.valueToUndefinedOrValue(field.multiSelect),
      isClearable: this.valueToUndefinedOrValue(field.isClearable),
      labelFieldName: this.valueToUndefinedOrValue(field.labelFieldName),
      showMultiSelectValueCommaSeparated: this.valueToUndefinedOrValue(field.showValueCommaSeparated)
    };

    return this.setFormFieldBaseProperties(schema, field);
  };

  convertArrayValidation = (validate: ValidateField): IArrayValidation[] => {
    const result: IArrayValidation[] = [];

    if (validate.isRequired) {
      result.push(new ArrayItemsRequiredValidation());
    }

    return result;
  };

  convertDependsOn = (field: AbstractFieldModel): FormControlDependsOnSchema[] | undefined => {
    let dependsOnSchema: FormControlDependsOnSchema[] | undefined = undefined;
    const fieldType = field.type;

    if (field.dependsOn && fieldType) {
      dependsOnSchema = field.dependsOn
        .map((item) => new DependsOnMapper().convert(item, fieldType))
        .filter(notNullOrUndefined);
    }
    if (field.nestedDependsOn && fieldType) {
      dependsOnSchema = [
        ...(dependsOnSchema ?? []),
        ...field.nestedDependsOn
          .map((item) => new DependsOnMapper().convertNested(item, fieldType))
          .filter(notNullOrUndefined)
      ];
    }
    return dependsOnSchema;
  };

  convertCollapse = (collapse?: CollapseSettings) => {
    if (collapse) {
      return {
        groupId: collapse.groupId,
        mode: collapse.mode
      };
    }
    return undefined;
  };
  convertUi = (ui: UIModel): FormControlUiSchema => ({
    flexDirection: this.convertFlexDirection(this.valueToUndefinedOrValue(ui.flexDirection)),
    formGroupfullWidth: this.valueToUndefinedOrValue(ui.formGroupfullWidth),
    inputDisplayStyle: this.convertInputDisplayStyle(this.valueToUndefinedOrValue(ui.inputDisplayStyle)),
    theme: this.convertTheme(this.valueToUndefinedOrValue(ui.theme)),
    showTitle: this.valueToUndefinedOrValue(ui.showTitle),
    isNumberedList: this.valueToUndefinedOrValue(ui.isNumberedList),
    readOverview: ui?.readOverview
      ? {
          hideWhenAllPropertiesAreNotGiven: this.valueToUndefinedOrValue(
            ui?.readOverview?.hideWhenAllPropertiesAreNotGiven
          ),
          render: this.valueToUndefinedOrValue(ui.readOverview?.render),
          isNumberedListItem: this.valueToUndefinedOrValue(ui.readOverview?.isNumberedListItem),
          collapse: this.convertCollapse(ui.readOverview?.collapse),
          hideWhenEmpty: this.valueToUndefinedOrValue(ui.readOverview?.hideWhenEmpty),
          isFilesOverview: this.valueToUndefinedOrValue(ui.readOverview?.isFilesOverview)
        }
      : undefined,
    formOverview: ui.formOverview
      ? {
          render: this.valueToUndefinedOrValue(ui.formOverview?.render),
          renderFormGroupWhenHidden: this.valueToUndefinedOrValue(ui.formOverview?.renderFormGroupWhenHidden)
        }
      : undefined
  });

  convertOnChange = (onChange: OnChangeModel): FormControlOnChangeSchema => {
    const configuratieService = new ConfiguratieService(this.apiUrl);
    return {
      timeoutInMilliseconds: onChange.triggerSearchAfterTimeout,
      triggerValidationAfterSetValues: onChange.triggerValidation,
      clearOnError: onChange.clearOnError,
      afterChangeGetValuesToBeSet: (_basePath: string, value: any) => {
        return new Promise((resolve, reject) => {
          if (!value) {
            resolve([]);
            return;
          }

          configuratieService.getData(onChange.url.replace("{value}", value)).then(
            (result) => {
              resolve([
                {
                  key: onChange.propertyPath,
                  value: {
                    ...((result as any).data as unknown as { [key: string]: string | undefined })
                  }
                }
              ]);
            },
            (reason) => {
              reject({
                result: [
                  {
                    key: onChange.propertyPath,
                    value: {
                      [onChange.validSearchResultPropertyName]: "false"
                    }
                  }
                ],
                error: reason.message
              });
            }
          );
        });
      }
    };
  };

  convertTheme = (theme?: string) => {
    return (["light", "light"] as const).find((item) => item === theme) ?? undefined;
  };

  convertInputDisplayStyle = (inputDisplayStyle?: string) => {
    return (["inline", "block"] as const).find((item) => item === inputDisplayStyle) ?? undefined;
  };

  convertFlexDirection = (direction?: string) => {
    return (
      (["row", "column", "row-reverse", "column-reverse"] as const).find((item) => item === direction) ??
      undefined
    );
  };

  convertBeforeFieldContent = (jsonBeforeFieldContent: string | null | undefined) => {
    if (!jsonBeforeFieldContent) {
      return undefined;
    }

    const parsedContent = ReactHtmlParser(jsonBeforeFieldContent);
    if (!parsedContent) {
      return undefined;
    }

    return ReactHtmlParser(`<div class='before-field-content'>${jsonBeforeFieldContent}</div>`);
  };

  convertPopupHint = (jsonPopupHint: jsonPopupHint | null | undefined): IPopupHint | undefined => {
    if (jsonPopupHint === undefined || jsonPopupHint === null) {
      return undefined;
    }

    return {
      icon: jsonPopupHint.icon,
      content: jsonPopupHint?.content ? ReactHtmlParser(jsonPopupHint.content) : undefined,
      text: jsonPopupHint.text
    };
  };

  getFileValidation = (field: FileFieldModel) => {
    let validation: IValidation[] = [new IsFileNotEmptyValidation()];
    if (field.extensions && field.extensions.length > 0) {
      validation.push(new HasFileExtensionValidation(field.extensions));
    }

    if (field.maxFiles) {
      validation.push(new HasMaxFilesUploadValidation(field.maxFiles));
    }

    let fileValidation = field.validate?.fileFieldValidation;
    if (!fileValidation) {
      return validation;
    }

    if (fileValidation?.maxFileSizeInMb) {
      let errorMessage: CustomErrorMessage | undefined = undefined;
      if (fileValidation?.errorMessage) {
        errorMessage = (_: string) => fileValidation?.errorMessage || "";
      }
      validation.push(new HasFileMaxSizeValidation(fileValidation.maxFileSizeInMb, errorMessage));
    }

    return validation;
  };
}
